import { type FormEvent, useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useCreateAssignment, useUpdateAssignment, countAddressesInArea } from "../api/assignments";
import { fetchCampaigns, fetchFlyerTypes, type Campaign, type CampaignFlyer } from "../api/campaigns";
import { useKretsMembers } from "../api/kretsMembers";
import { normalizeAssignmentStatus } from "../api/assignmentStatus";
import type { AssignmentResponse, AssignmentStatus, AssignmentArea } from "../api/types";
import { useMunicipalities } from "../api/map";
import { useMapContextOptional } from "../map/MapContext";
import { getDrawnPolygonArea } from "./PolygonDrawer";
import "./assignment-form.css";

interface AssignmentFormProps {
    assignment?: AssignmentResponse;
    defaultMunicipalityPolygonId?: string;
    onSuccess?: () => void;
    onCancel?: () => void;
    /** Edit mode only — the list keeps the confirm dialog and the mutation. */
    onDelete?: () => void;
    isDeleting?: boolean;
    /** Edit mode only — exports this one assignment as an A4 landscape PNG. */
    onExportA4Image?: () => void;
    isExportingA4Image?: boolean;
}

interface FormState {
    municipalityPolygonId: string;
    campaignId: string;
    name: string;
    kretsMemberId: string;
    status: AssignmentStatus;
    postalCodeFilter: string;
    flyerTypeId: string;
    targetAddressCount: string;
    dueDate: string;
    notes: string;
    area: AssignmentArea | null;
}

const hasValidPolygon = (area: AssignmentArea | null): area is AssignmentArea => {
    const ring = area?.coordinates?.[0];
    return Array.isArray(ring) && ring.length >= 4;
};

const getInitialState = (assignment?: AssignmentResponse, defaultMunicipalityPolygonId?: string): FormState => {
    // Auto-set due date to +1 month from today for new assignments
    const getDefaultDueDate = () => {
        if (assignment?.dueDate) {
            return new Date(assignment.dueDate).toISOString().split("T")[0];
        }
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return nextMonth.toISOString().split("T")[0];
    };

    return {
        municipalityPolygonId: assignment?.municipalityPolygonId || defaultMunicipalityPolygonId || "",
        campaignId: assignment?.campaignId || "",
        name: assignment?.name || "",
        kretsMemberId: assignment?.kretsMemberId || "",
        status: normalizeAssignmentStatus(assignment?.status),
        postalCodeFilter: assignment?.postalCodeFilter || "",
        flyerTypeId: assignment?.flyerTypeId || "",
        targetAddressCount: assignment?.targetAddressCount?.toString() || "",
        dueDate: getDefaultDueDate(),
        notes: assignment?.notes || "",
        area: assignment?.area || null
    };
};

export const AssignmentForm = ({
    assignment,
    defaultMunicipalityPolygonId,
    onSuccess,
    onCancel,
    onDelete,
    isDeleting = false,
    onExportA4Image,
    isExportingA4Image = false
}: AssignmentFormProps): JSX.Element => {
    const { t } = useTranslation();
    const [formState, setFormState] = useState<FormState>(getInitialState(assignment, defaultMunicipalityPolygonId));
    const mapContext = useMapContextOptional();
    const isEditMode = !!assignment;

    const [optionalOpen, setOptionalOpen] = useState(
        () => !!(assignment?.campaignId || assignment?.kretsMemberId || assignment?.distributorId || (assignment?.status && assignment?.status !== "planned") || assignment?.flyerTypeId || assignment?.dueDate)
    );

    // Map functionality is optional - form can work in admin without map
    const isDrawingPolygon = mapContext?.isDrawingPolygon ?? false;
    const polygonArea = mapContext?.polygonArea ?? null;
    const startPolygonDrawing = mapContext?.startPolygonDrawing;
    const stopPolygonDrawing = mapContext?.stopPolygonDrawing;
    const setPolygonArea = mapContext?.setPolygonArea;

    // Fetch campaigns
    const { data: campaigns = [] } = useQuery<Campaign[]>({
        queryKey: ["campaigns"],
        queryFn: fetchCampaigns,
        staleTime: 5 * 60 * 1000
    });

    // Fetch krets members
    const { data: kretsMembers = [] } = useKretsMembers();

    // Fetch flyer types for selected campaign
    const { data: campaignFlyers = [] } = useQuery<CampaignFlyer[]>({
        queryKey: ["flyerTypes", formState.campaignId],
        queryFn: () => fetchFlyerTypes(formState.campaignId),
        enabled: !!formState.campaignId,
        staleTime: 5 * 60 * 1000
    });

    const municipalitiesQuery = useMunicipalities(
        { limit: 2000, includeGeometry: false },
        null,
        { enabled: true }
    );
    const municipalities = useMemo(
        () =>
            (municipalitiesQuery.data?.features ?? [])
                .map((feature) => ({
                    id: String(feature.id ?? feature.properties.municipalityPolygonId),
                    name: feature.properties.name
                }))
                .sort((a, b) => a.name.localeCompare(b.name, "sv")),
        [municipalitiesQuery.data?.features]
    );

    // Flyer options are the selected campaign's flyers (id = CampaignFlyer.Id,
    // the flyer key everything is keyed on since the FlyerType conversion).
    const flyerTypes = useMemo(
        () =>
            formState.campaignId
                ? campaignFlyers.map((cf) => ({ id: cf.id, name: cf.name ?? "Okänt flygblad" }))
                : [],
        [campaignFlyers, formState.campaignId]
    );

    const createMutation = useCreateAssignment();
    const updateMutation = useUpdateAssignment();

    const mutation = isEditMode ? updateMutation : createMutation;
    const isPending = mutation.isPending;
    const isError = mutation.isError;

    // Reset form when assignment changes
    useEffect(() => {
        setFormState(getInitialState(assignment, defaultMunicipalityPolygonId));
    }, [assignment, defaultMunicipalityPolygonId]);

    // Sync polygon area from map context to form state
    useEffect(() => {
        if (!mapContext) {
            return;
        }

        const mapHasPolygon = hasValidPolygon(polygonArea);
        const formHasPolygon = hasValidPolygon(formState.area);

        // Keep existing assignment area on init/edit until map explicitly has a polygon or user clears.
        if (!isDrawingPolygon && !mapHasPolygon && formHasPolygon) {
            return;
        }

        if (polygonArea !== formState.area) {
            setFormState((prev) => ({
                ...prev,
                area: polygonArea
            }));
        }
    }, [mapContext, polygonArea, formState.area, isDrawingPolygon]);

    const isDrawingPolygonRef = useRef(isDrawingPolygon);

    useEffect(() => {
        isDrawingPolygonRef.current = isDrawingPolygon;
    }, [isDrawingPolygon]);

    useEffect(() => {
        return () => {
            if (isDrawingPolygonRef.current) {
                stopPolygonDrawing?.();
            }
        };
    }, [stopPolygonDrawing]);

    // The area to count addresses for — never while the user is still editing the polygon.
    const countableArea = useMemo(
        () => (!isDrawingPolygon && hasValidPolygon(formState.area) ? formState.area : null),
        [formState.area, isDrawingPolygon]
    );

    // Keying the query on the coordinates means a finished area edit always counts
    // the new polygon (and re-uses the cached count if the geometry is unchanged).
    const addressCountQuery = useQuery({
        queryKey: [
            "assignmentAddressCount",
            formState.campaignId || null,
            countableArea ? JSON.stringify(countableArea.coordinates) : null
        ],
        queryFn: () =>
            countAddressesInArea({
                campaignId: formState.campaignId || undefined,
                area: countableArea as AssignmentArea
            }),
        enabled: !!countableArea,
        staleTime: 5 * 60 * 1000,
        retry: 1
    });

    const possibleAddressCount = countableArea ? addressCountQuery.data?.count ?? null : null;
    const isCountingAddresses = !!countableArea && addressCountQuery.isFetching;
    const countAddressesError = addressCountQuery.isError ? t("assignments.errors.addressCountFailed") : null;

    // Prefill the target count from the first successful count, never overwrite a manual value.
    useEffect(() => {
        if (possibleAddressCount === null) {
            return;
        }
        setFormState((prev) =>
            prev.targetAddressCount ? prev : { ...prev, targetAddressCount: possibleAddressCount.toString() }
        );
    }, [possibleAddressCount]);

    // Start polygon drawing when user clicks "Draw Area"
    const handleStartDrawing = () => {
        startPolygonDrawing?.(formState.area || undefined, (area) => {
            setFormState((prev) => ({
                ...prev,
                area: area || null
            }));
        });
    };

    // Stop polygon drawing and restore read-only area highlight so the result is visible on the map
    const handleStopDrawing = () => {
        // Read the geometry from Terra Draw itself — the map context can still hold the
        // pre-edit polygon if the last change event has not made it into state yet.
        const finalArea = getDrawnPolygonArea() ?? polygonArea;
        stopPolygonDrawing?.();
        if (finalArea && hasValidPolygon(finalArea)) {
            // Commit the finished geometry to both form and map context (same reference, so the
            // sync effect is a no-op). The address count is keyed on it, which is what makes
            // "Möjliga adresser" reflect the edited area.
            setFormState((prev) => (prev.area === finalArea ? prev : { ...prev, area: finalArea }));
            setPolygonArea?.(finalArea);
            mapContext?.setHighlightedAssignmentArea(finalArea, {
                assignmentId: assignment?.id
            });
        }
    };

    // Clear area
    const handleClearArea = () => {
        if (isDrawingPolygon) {
            stopPolygonDrawing?.();
        }

        setPolygonArea?.(null);
        mapContext?.setHighlightedAssignmentArea(null);
        setFormState((prev) => ({
            ...prev,
            area: null
        }));
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const sharedFields = {
            municipalityPolygonId: formState.municipalityPolygonId,
            name: formState.name,
            campaignId: formState.campaignId || undefined,
            kretsMemberId: formState.kretsMemberId || undefined,
            status: formState.status,
            area: formState.area || undefined,
            postalCodeFilter: formState.postalCodeFilter || undefined,
            flyerTypeId: formState.flyerTypeId || undefined,
            targetAddressCount: formState.targetAddressCount ? parseInt(formState.targetAddressCount, 10) : undefined,
            dueDate: formState.dueDate || undefined,
            notes: formState.notes || undefined
        };

        if (isEditMode && assignment) {
            updateMutation.mutate(
                {
                    id: assignment.id,
                    request: {
                        ...sharedFields,
                        clearCampaign: !!assignment.campaignId && !formState.campaignId,
                        clearKretsMember: !!assignment.kretsMemberId && !formState.kretsMemberId
                    }
                },
                {
                    onSuccess: () => {
                        onSuccess?.();
                    }
                }
            );
        } else {
            createMutation.mutate(sharedFields, {
                onSuccess: () => {
                    setFormState(getInitialState(undefined, defaultMunicipalityPolygonId));
                    onSuccess?.();
                }
            });
        }
    };

    // Debug: Log form state to help diagnose visibility issues

    return (
        <form className="assignment-form" onSubmit={handleSubmit}>
            <label>
                {t("assignments.fields.municipality", "Kommun")}
                <select
                    name="municipalityPolygonId"
                    value={formState.municipalityPolygonId}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            municipalityPolygonId: event.target.value
                        }))
                    }
                    required
                >
                    <option value="">{t("assignments.placeholders.municipality", "Välj kommun")}</option>
                    {municipalities.map((municipality) => (
                        <option key={municipality.id} value={municipality.id}>
                            {municipality.name}
                        </option>
                    ))}
                </select>
            </label>

            <label>
                {t("assignments.fields.name")}
                <input
                    name="name"
                    type="text"
                    maxLength={200}
                    value={formState.name}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            name: event.target.value
                        }))
                    }
                    required
                    placeholder={t("assignments.placeholders.name")}
                />
            </label>

            {mapContext && (
                <div className="assignment-form__area-section">
                    <label>
                        {t("assignments.fields.area")}
                        <div className="assignment-form__area-controls">
                            {!isDrawingPolygon ? (
                                <button
                                    type="button"
                                    className="assignment-form__draw-button"
                                    onClick={handleStartDrawing}
                                >
                                    {hasValidPolygon(formState.area)
                                        ? t("assignments.actions.editArea", "Redigera yta")
                                        : t("assignments.actions.drawArea")}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="assignment-form__draw-button"
                                    onClick={handleStopDrawing}
                                >
                                    {t("assignments.actions.finishDrawing")}
                                </button>
                            )}
                            {formState.area && (
                                <button
                                    type="button"
                                    className="assignment-form__clear-area-button"
                                    onClick={handleClearArea}
                                >
                                    {t("assignments.actions.clearArea")}
                                </button>
                            )}
                        </div>
                        {isDrawingPolygon && (
                            <p className="assignment-form__area-status assignment-form__area-status--info">
                                {t("assignments.info.drawingInstructions")}
                            </p>
                        )}
                        {!isDrawingPolygon && formState.area && (
                            <p className="assignment-form__area-status">{t("assignments.info.areaSet")}</p>
                        )}
                        {!isDrawingPolygon && !formState.area && (
                            <p className="assignment-form__area-status assignment-form__area-status--warning">
                                {t("assignments.info.areaRequired")}
                            </p>
                        )}
                    </label>
                </div>
            )}

            <details
                className="assignment-form__optional-section"
                open={optionalOpen}
                onToggle={(e) => setOptionalOpen(e.currentTarget.open)}
            >
                <summary className="assignment-form__optional-summary">
                    {t("assignments.fields.optionalFields", "Valfria fält")}
                </summary>

                <label>
                    {t("assignments.fields.campaign")}
                    <select
                        name="campaignId"
                        value={formState.campaignId}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                campaignId: event.target.value,
                                flyerTypeId: ""
                            }))
                        }
                    >
                        <option value="">{t("assignments.placeholders.campaign")}</option>
                        {campaigns.map((campaign) => (
                            <option key={campaign.id} value={campaign.id}>
                                {campaign.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    {t("assignments.fields.distributor")}
                    <select
                        name="kretsMemberId"
                        value={formState.kretsMemberId}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                kretsMemberId: event.target.value
                            }))
                        }
                    >
                        <option value="">{t("assignments.placeholders.distributor")}</option>
                        {kretsMembers.map((member) => (
                            <option key={member.id} value={member.id}>
                                {member.name}{member.teamName ? ` (${member.teamName})` : ""}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    {t("assignments.fields.status")}
                    <select
                        name="status"
                        value={formState.status}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                status: event.target.value as AssignmentStatus
                            }))
                        }
                    >
                        <option value="planned">{t("assignments.status.planned")}</option>
                        <option value="inProgress">{t("assignments.status.inProgress")}</option>
                        <option value="completed">{t("assignments.status.completed")}</option>
                    </select>
                </label>

                <label>
                    {t("assignments.fields.flyerType")}
                    <select
                        name="flyerTypeId"
                        value={formState.flyerTypeId}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                flyerTypeId: event.target.value
                            }))
                        }
                    >
                        <option value="">{t("assignments.placeholders.flyerType")}</option>
                        {flyerTypes.map((flyerType) => (
                            <option key={flyerType.id} value={flyerType.id}>
                                {flyerType.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    {t("assignments.fields.dueDate")}
                    <input
                        name="dueDate"
                        type="date"
                        value={formState.dueDate}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                dueDate: event.target.value
                            }))
                        }
                    />
                </label>
            </details>

            {formState.area ? (
                <label>
                    {t("assignments.fields.possibleAddresses")}
                    <input
                        name="possibleAddressCount"
                        type="text"
                        value={possibleAddressCount !== null
                            ? possibleAddressCount.toString()
                            : t("assignments.placeholders.calculating")}
                        readOnly
                        className="assignment-form__readonly-input"
                        disabled={isCountingAddresses || possibleAddressCount === null}
                    />
                    {(isCountingAddresses || possibleAddressCount === null) && !countAddressesError && (
                        <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {t("assignments.info.calculatingAddresses")}
                        </small>
                    )}
                    {countAddressesError && (
                        <small role="alert" style={{ color: '#b42318', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {countAddressesError}
                        </small>
                    )}
                </label>
            ) : null}

            <label>
                {t("assignments.fields.targetCount")}
                <input
                    name="targetAddressCount"
                    type="number"
                    min={0}
                    value={formState.targetAddressCount}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            targetAddressCount: event.target.value
                        }))
                    }
                    placeholder={t("assignments.placeholders.targetCount")}
                />
            </label>

            <div className="assignment-form__actions">
                <button type="submit" disabled={isPending}>
                    {isPending ? t("assignments.actions.saving") : t("assignments.actions.save")}
                </button>
                {onCancel && (
                    <button type="button" onClick={onCancel} disabled={isPending || isDeleting}>
                        {t("assignments.actions.cancel")}
                    </button>
                )}
                {isEditMode && onExportA4Image && (
                    <button
                        type="button"
                        className="assignment-form__export-button"
                        onClick={onExportA4Image}
                        disabled={isPending || isDeleting || isExportingA4Image || !hasValidPolygon(assignment?.area ?? null)}
                        title="Exportera detta uppdrag som A4 liggande PNG (sparad yta)"
                    >
                        {isExportingA4Image ? "Exporterar..." : "Exportera A4-bild"}
                    </button>
                )}
                {isEditMode && onDelete && (
                    <button
                        type="button"
                        className="assignment-form__delete-button"
                        onClick={onDelete}
                        disabled={isPending || isDeleting}
                    >
                        {isDeleting ? t("assignments.actions.deleting") : t("assignments.actions.delete")}
                    </button>
                )}
            </div>

            {isError && (
                <p role="alert" className="assignment-form__error">
                    {isEditMode
                        ? t("assignments.errors.updateFailed")
                        : t("assignments.errors.createFailed")}
                </p>
            )}
        </form>
    );
};